import clienteAxios from "config/axios";
import { useEffect, useMemo, useState } from "react";
import LineSpinner from "components/spinners/LineSpinner";
import { useT } from "i18n";

const ESTADOS_CIVILES = ["Soltero", "Casado", "Divorciado", "Viudo", "Unión libre"];
const TIPOS_DOCUMENTO = ["Cédula/DNI", "Pasaporte"];
const SEXOS = ["Masculino", "Femenino"];
// El valor que se envía al servidor sigue en español; solo cambia el texto visible.
const ETIQ_OPCION = {
  "Cédula/DNI": "tarjeta.docCedula", "Pasaporte": "tarjeta.docPasaporte",
  "Masculino": "tarjeta.sexoM", "Femenino": "tarjeta.sexoF",
  "Soltero": "tarjeta.civilSoltero", "Casado": "tarjeta.civilCasado", "Divorciado": "tarjeta.civilDivorciado",
  "Viudo": "tarjeta.civilViudo", "Unión libre": "tarjeta.civilUnion",
};
const PAIS_PRIORITARIO = "Panamá";

const hoy = () => new Date().toISOString().slice(0, 10);

const Campo = ({ id, etiqueta, children }) => (
  <div className="p7-campo">
    <label htmlFor={id}>{etiqueta}</label>
    {children}
  </div>
);

function FormEdit({ data, onUpdate, onDataChange, errorMessage, editState, setEditState, loading }) {
  const { t, ts } = useT();
  const [paises, setPaises] = useState([]);
  const nombreInicial = (data?.name || "").split(" ");
  const [vista, setVista] = useState({ nombre: nombreInicial[0] || "", apellido: nombreInicial[1] || "" });
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    let activo = true;
    clienteAxios.post("/api/paises/list")
      .then((r) => { if (activo) setPaises(r.data.data || []); })
      .catch((error) => console.error(error));
    return () => { activo = false; };
  }, []);

  // Panamá primero y el resto en orden alfabético
  const listaPaises = useMemo(() => {
    const orden = [...paises].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es"));
    const pa = orden.filter((c) => c.name === PAIS_PRIORITARIO);
    return pa.concat(orden.filter((c) => c.name !== PAIS_PRIORITARIO));
  }, [paises]);

  useEffect(() => {
    if (errorMessage && editState) setShowModal(true);
  }, [errorMessage, editState]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(data);
  };

  const cerrarModal = () => {
    setShowModal(false);
    if (setEditState) setEditState();
  };

  const alCambiar = (e) => {
    if (e.target.name === "first_name") setVista((v) => ({ ...v, nombre: e.target.value }));
    if (e.target.name === "last_name") setVista((v) => ({ ...v, apellido: e.target.value }));
    onDataChange(e);
  };

  const titular = `${vista.nombre} ${vista.apellido}`.trim().toUpperCase() || t("tarjeta.tuNombre");

  const exito = /éxito|success|sucesso/i.test(errorMessage || "");
  const nombre = (data?.name || "").split(" ");

  const opcionesPais = (
    <>
      <option value="">{t("tarjeta.selPais")}</option>
      {listaPaises.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
    </>
  );

  return (
    <div className="p7-tarjeta-form">
      <aside className="p7-tf-lado" aria-hidden="true">
        <div className="p7-card3d">
          <img className="p7-card3d-logo" src={`${process.env.PUBLIC_URL}/panther-gold.svg`} alt="" />
          <span className="p7-card3d-marca">VISA</span>
          <span className="p7-card3d-chip" />
          <span className="p7-card3d-num">••••&nbsp;&nbsp;••••&nbsp;&nbsp;••••&nbsp;&nbsp;••••</span>
          <div className="p7-card3d-pie">
            <div><small>{t("tarjeta.titular")}</small><strong>{titular}</strong></div>
            <div><small>{t("tarjeta.tarjeta")}</small><strong>{t("tarjeta.prepago")}</strong></div>
          </div>
        </div>
        <p className="p7-tf-nota">{t("tarjeta.notaEmision")}</p>
      </aside>

      <form onSubmit={handleSubmit}>
        <header className="p7-tf-head">
          <h2>{t("tarjeta.solicitaTitulo")}</h2>
          <p>{t("tarjeta.solicitaAyuda")}</p>
        </header>

        <fieldset className="p7-tf-sec">
          <legend>{t("tarjeta.secPersonales")}</legend>
          <div className="p7-tf-grid">
            <Campo id="first_name" etiqueta={t("tarjeta.lblNombre")}>
              <input id="first_name" name="first_name" type="text" onChange={alCambiar} defaultValue={nombre[0] || ""} autoComplete="given-name" required />
            </Campo>
            <Campo id="last_name" etiqueta={t("tarjeta.lblApellido")}>
              <input id="last_name" name="last_name" type="text" onChange={alCambiar} defaultValue={nombre[1] || ""} autoComplete="family-name" required />
            </Campo>
            <Campo id="document_type" etiqueta={t("tarjeta.lblTipoDoc")}>
              <select id="document_type" name="document_type" defaultValue={data?.document_type || TIPOS_DOCUMENTO[0]} onChange={onDataChange}>
                {TIPOS_DOCUMENTO.map((d) => <option key={d} value={d}>{t(ETIQ_OPCION[d])}</option>)}
              </select>
            </Campo>
            <Campo id="cedula" etiqueta={t("tarjeta.lblNumDoc")}>
              <input id="cedula" name="cedula" type="text" onChange={onDataChange} defaultValue={data?.identity || ""} required />
            </Campo>
            <Campo id="birthDate" etiqueta={t("tarjeta.lblNacimiento")}>
              <input id="birthDate" name="birthDate" type="date" min="1900-01-01" max={hoy()} onChange={onDataChange} defaultValue={data?.birthday_date || ""} required />
            </Campo>
            <Campo id="genero" etiqueta={t("tarjeta.lblSexo")}>
              <select id="genero" name="genero" defaultValue={data?.genero || ""} onChange={onDataChange} required>
                <option value="">{t("tarjeta.selecciona")}</option>
                {SEXOS.map((x) => <option key={x} value={x}>{t(ETIQ_OPCION[x])}</option>)}
              </select>
            </Campo>
            <Campo id="estadocivil" etiqueta={t("tarjeta.lblEstadoCivil")}>
              <select id="estadocivil" name="estadocivil" defaultValue={data?.estadocivil || ""} onChange={onDataChange} required>
                <option value="">{t("tarjeta.selecciona")}</option>
                {ESTADOS_CIVILES.map((x) => <option key={x} value={x}>{t(ETIQ_OPCION[x])}</option>)}
              </select>
            </Campo>
            <Campo id="nacionalidad" etiqueta={t("tarjeta.lblNacionalidad")}>
              <select id="nacionalidad" name="nacionalidad" defaultValue={data?.address_country || ""} onChange={onDataChange} required>
                {opcionesPais}
              </select>
            </Campo>
            <Campo id="profesion" etiqueta={t("tarjeta.lblProfesion")}>
              <input id="profesion" name="profesion" type="text" onChange={onDataChange} defaultValue={data?.comercial_name || ""} required />
            </Campo>
          </div>
        </fieldset>

        <fieldset className="p7-tf-sec">
          <legend>{t("tarjeta.secContacto")}</legend>
          <div className="p7-tf-grid">
            <Campo id="email" etiqueta={t("tarjeta.lblCorreo")}>
              <input id="email" name="email" type="email" onChange={onDataChange} defaultValue={data?.email || ""} autoComplete="email" required />
            </Campo>
            <Campo id="phone" etiqueta={t("tarjeta.lblTelefono")}>
              <input id="phone" name="phone" type="tel" onChange={onDataChange} defaultValue={data?.telephone || ""} placeholder={t("tarjeta.phTelefono")} autoComplete="tel" required />
            </Campo>
          </div>
        </fieldset>

        <fieldset className="p7-tf-sec">
          <legend>{t("tarjeta.secResidencia")}</legend>
          <div className="p7-tf-grid">
            <Campo id="country_id" etiqueta={t("tarjeta.lblPaisRes")}>
              <select id="country_id" name="country_id" defaultValue={data?.address_country || ""} onChange={onDataChange} required>
                {opcionesPais}
              </select>
            </Campo>
            <Campo id="state" etiqueta={t("tarjeta.lblProvincia")}>
              <input id="state" name="state" type="text" onChange={onDataChange} defaultValue={data?.address_state || ""} required />
            </Campo>
            <Campo id="city" etiqueta={t("tarjeta.lblCiudad")}>
              <input id="city" name="city" type="text" onChange={onDataChange} defaultValue={data?.address_city || ""} required />
            </Campo>
            <Campo id="zipCode" etiqueta={t("tarjeta.lblPostal")}>
              <input id="zipCode" name="zipCode" type="text" onChange={onDataChange} defaultValue={data?.address_zipcode || ""} required />
            </Campo>
            <Campo id="address_street" etiqueta={t("tarjeta.lblCalle")}>
              <input id="address_street" name="address_street" type="text" onChange={onDataChange} defaultValue={data?.address_street || ""} autoComplete="address-line1" required />
            </Campo>
            <Campo id="address_number" etiqueta={t("tarjeta.lblComplemento")}>
              <input id="address_number" name="address_number" type="text" onChange={onDataChange} defaultValue={data?.address_number || ""} autoComplete="address-line2" />
            </Campo>
          </div>
        </fieldset>

        <div className="p7-tf-actions">
          <button type="submit" className="p7-btn-gold" disabled={loading}>
            {loading ? <LineSpinner /> : t("tarjeta.confirmarDatos")}
          </button>
        </div>
      </form>

      {showModal && (
        <div className="p7-wallet-overlay" role="dialog" aria-modal="true" onClick={cerrarModal}>
          <div className="p7-wallet-card" onClick={(e) => e.stopPropagation()}>
            <h4 style={{ color: exito ? "#5fc99a" : "#e5736f" }}>{exito ? t("tarjeta.solicitudEnviada") : t("tarjeta.revisaDatos")}</h4>
            <p className="p7-wallet-help" style={{ fontSize: ".95rem", color: "#e8eaf0" }}>{ts(errorMessage)}</p>
            <div className="p7-wallet-actions">
              <button type="button" className="p7-btn-gold" onClick={cerrarModal}>{t("alerta.aceptar")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FormEdit;
